import api from "./api";

export interface CreateStockItemData {
  medicineId: string;
  name: string;
  genericName?: string;
  category:
    | "tablet"
    | "capsule"
    | "syrup"
    | "injection"
    | "cream"
    | "drops"
    | "inhaler"
    | "other";
  manufacturer: string;
  supplier: string;
  batchNumber: string;
  expiryDate: string;
  manufacturingDate: string;
  quantity: {
    current: number;
    minimum: number;
    unit: "pieces" | "bottles" | "boxes" | "strips" | "vials";
  };
  pricing: {
    purchasePrice: number;
    sellingPrice: number;
    mrp: number;
    discount?: number;
    taxPercent?: number;
    tax?: number;
  };
  location?: {
    rack?: string;
    shelf?: string;
    position?: string;
  };
  alertLowStock?: boolean;
  notes?: string;
}

export interface UpdateStockItemData extends Partial<CreateStockItemData> {}

export interface StockFilters {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  supplier?: string;
  lowStock?: string;
  expired?: string;
  sortBy?: string;
  sortOrder?: string;
}

export interface UpdateQuantityData {
  quantity: number;
  operation: "add" | "subtract";
}

class StockService {
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private readonly CACHE_TTL = 30000; // 30 seconds cache

  private getCacheKey = (endpoint: string, params?: any): string => {
    return `${endpoint}:${JSON.stringify(params || {})}`;
  };

  private isValidCache = (timestamp: number): boolean => {
    return Date.now() - timestamp < this.CACHE_TTL;
  };

  getStockItems = async (filters?: StockFilters) => {
    console.log("stockService.getStockItems called with filters:", filters);
    const cacheKey = this.getCacheKey("stock", filters);
    const cached = this.cache.get(cacheKey);

    if (cached && this.isValidCache(cached.timestamp)) {
      console.log("Returning cached stock items");
      return cached.data;
    }

    console.log("Making API call to /stock with params:", filters);
    const response = await api.get("/stock", { params: filters });
    console.log("API response received:", response.data);
    this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
    return response.data;
  };

  getStockItem = async (id: string) => {
    const response = await api.get(`/stock/${id}`);
    return response.data;
  };

  createStockItem = async (data: CreateStockItemData) => {
    const response = await api.post("/stock", data);
    this.clearCache(); // Clear cache after creating new item
    return response.data;
  };

  updateStockItem = async (id: string, data: UpdateStockItemData) => {
    const response = await api.put(`/stock/${id}`, data);
    this.clearCache(); // Clear cache after updating item
    return response.data;
  };

  updateStockQuantity = async (id: string, data: UpdateQuantityData) => {
    const response = await api.put(`/stock/${id}/quantity`, data);
    this.clearCache(); // Clear cache after updating quantity
    return response.data;
  };

  deleteStockItem = async (id: string) => {
    const response = await api.delete(`/stock/${id}`);
    this.clearCache(); // Clear cache after deleting item
    return response.data;
  };

  getStockStats = async () => {
    console.log("stockService.getStockStats called");
    const cacheKey = this.getCacheKey("stock-stats");
    const cached = this.cache.get(cacheKey);

    if (cached && this.isValidCache(cached.timestamp)) {
      console.log("Returning cached stock stats");
      return cached.data;
    }

    console.log("Making API call to /stock/stats");
    const response = await api.get("/stock/stats");
    console.log("Stock stats API response received:", response.data);
    this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
    return response.data;
  };

  getStockAlerts = async () => {
    console.log("stockService.getStockAlerts called");
    const cacheKey = this.getCacheKey("stock-alerts");
    const cached = this.cache.get(cacheKey);

    if (cached && this.isValidCache(cached.timestamp)) {
      console.log("Returning cached stock alerts");
      return cached.data;
    }

    console.log("Making API call to /stock/alerts");
    const response = await api.get("/stock/alerts");
    console.log("Stock alerts API response received:", response.data);
    this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
    return response.data;
  };

  getMovements = async (params?: {
    page?: number;
    limit?: number;
    stock?: string;
    medicine?: string;
    type?: string;
  }) => {
    return api.get("/stock/movements", { params });
  };

  // Clear cache when data is modified
  clearCache = () => {
    this.cache.clear();
  };
}

const stockService = new StockService();
export default stockService;
